import { X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useAllowProductActivationMutation, useRejectProductActivationMutation } from "@/api/query-hooks";
import type { ProductActivationRecord } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatDateTime } from "@/lib/date";
import { MonitoringApprovalAction } from "@/features/licensing/monitoring-approval-action";
import { ClientIdentity } from "@/features/licensing/client-identity";

export function ProductActivationTable({ items, showClient = true }: { items: ProductActivationRecord[]; showClient?: boolean }) {
  const { t, i18n } = useTranslation(["licensing", "common"]);
  const allow = useAllowProductActivationMutation();
  const reject = useRejectProductActivationMutation();
  const [rejecting, setRejecting] = useState<ProductActivationRecord | null>(null);
  const [reason, setReason] = useState("");

  const allowItem = async (item: ProductActivationRecord) => {
    try {
      await allow.mutateAsync(
      { clientId: item.clientId, requestId: item.id, expectedRevision: item.revision },
      );
      toast.success(t("activation.allowed"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("common:states.error"));
      throw error;
    }
  };

  const rejectItem = () => {
    if (!rejecting || !reason.trim()) return;
    reject.mutate(
      { clientId: rejecting.clientId, requestId: rejecting.id, expectedRevision: rejecting.revision, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success(t("activation.rejected"));
          setRejecting(null);
          setReason("");
        },
        onError: (error) => toast.error(error instanceof Error ? error.message : t("common:states.error"))
      }
    );
  };

  return (
    <>
      <div className="overflow-x-auto max-md:overflow-visible">
        <table className="w-full border-collapse text-sm max-md:block">
          <thead className="bg-muted text-left text-xs uppercase text-muted-foreground max-md:hidden">
            <tr>
              {showClient ? <th className="px-3 py-2">{t("columns.client")}</th> : null}
              <th className="px-3 py-2">{t("columns.product")}</th>
              <th className="px-3 py-2">{t("columns.device")}</th>
              <th className="px-3 py-2">{t("columns.version")}</th>
              <th className="px-3 py-2">{t("columns.requested")}</th>
              <th className="px-3 py-2 text-right">{t("columns.actions")}</th>
            </tr>
          </thead>
          <tbody className="max-md:block max-md:space-y-3 max-md:p-3">
            {items.map((item) => (
              <tr key={item.id} data-testid="product-activation-item" className="border-t align-top max-md:block max-md:rounded-md max-md:border max-md:p-3">
                {showClient ? (
                  <td className="px-3 py-2 max-md:block max-md:px-0 max-md:py-1">
                    <MobileLabel>{t("columns.client")}</MobileLabel>
                    <ClientIdentity clientId={item.clientId} />
                  </td>
                ) : null}
                <td className="px-3 py-2 font-medium max-md:block max-md:px-0 max-md:py-1"><MobileLabel>{t("columns.product")}</MobileLabel>{item.appId}</td>
                <td className="px-3 py-2 max-md:block max-md:px-0 max-md:py-1">
                  <MobileLabel>{t("columns.device")}</MobileLabel>
                  <div className="font-medium">{item.hostname || t("activation.unnamedDevice")}</div>
                  <div className="text-xs text-muted-foreground">{item.os} / {item.arch}</div>
                </td>
                <td className="px-3 py-2 max-md:block max-md:px-0 max-md:py-1">
                  <MobileLabel>{t("columns.version")}</MobileLabel>
                  <div>{item.currentVersion}</div>
                  <div className="text-xs text-muted-foreground">{item.releaseChannel}</div>
                </td>
                <td className="px-3 py-2 max-md:block max-md:px-0 max-md:py-1">
                  <MobileLabel>{t("columns.requested")}</MobileLabel>
                  <div>{formatDateTime(item.createdAt, i18n.language)}</div>
                  <div className="text-xs text-muted-foreground">{t("activation.expires", { value: formatDateTime(item.expiresAt, i18n.language) })}</div>
                </td>
                <td className="px-3 py-2 max-md:block max-md:px-0 max-md:pt-2">
                  <MobileLabel>{t("columns.actions")}</MobileLabel>
                  <div className="flex flex-wrap justify-end gap-2 max-md:justify-start">
                    <MonitoringApprovalAction
                      appId={item.appId}
                      clientId={item.clientId}
                      relationKind="PRODUCT_ACTIVATION"
                      relationId={item.id}
                      os={item.os}
                      hostname={item.hostname || t("activation.unnamedDevice")}
                      approveLabel={t("activation.allow")}
                      disabled={allow.isPending || reject.isPending}
                      onApprove={() => allowItem(item)}
                    />
                    <Button size="sm" variant="outline" onClick={() => setRejecting(item)} disabled={allow.isPending || reject.isPending}>
                      <X className="h-4 w-4" />{t("activation.reject")}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={Boolean(rejecting)} onOpenChange={(open) => { if (!open) { setRejecting(null); setReason(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("activation.rejectTitle")}</DialogTitle>
            <DialogDescription>
              {rejecting ? t("activation.rejectDescription", { product: rejecting.appId, host: rejecting.hostname }) : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="activation-reject-reason">{t("activation.reason")}</Label>
            <Input id="activation-reject-reason" value={reason} maxLength={500} onChange={(event) => setReason(event.target.value)} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setRejecting(null); setReason(""); }}>{t("common:actions.cancel")}</Button>
            <Button variant="destructive" onClick={rejectItem} disabled={!reason.trim() || reject.isPending}>{t("activation.reject")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function MobileLabel({ children }: { children: ReactNode }) {
  return <span className="mb-1 block text-xs font-normal uppercase text-muted-foreground md:hidden">{children}</span>;
}
